import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, Instagram, Settings, Sparkles, ChevronRight,
  ArrowUpDown, SlidersHorizontal, X, Download, Printer,
  ShieldCheck, HelpCircle, ShoppingBag,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { PackCard } from "@/components/PackCard";
import { PackDetailDrawer } from "@/components/PackDetailDrawer";
import {
  captureSource, trackEvent, getVisitorToken, getSessionId,
} from "@/lib/analytics";
import api from "@/lib/api";
import { toast } from "sonner";
import debounce from "lodash/debounce";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PACKS_PER_PAGE = 6;

const SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "price_low", label: "Price: Low → High" },
  { value: "price_high", label: "Price: High → Low" },
  { value: "age_low", label: "Age: Youngest First" },
  { value: "age_high", label: "Age: Oldest First" },
];

const FEATURE_ICONS = {
  download: Download, printer: Printer, lock: ShieldCheck, help: HelpCircle,
  clock: Sparkles, setup: Sparkles, story: Sparkles,
};

function parseMinAge(r) { const m = r?.match(/(\d+)/); return m ? parseInt(m[1], 10) : 0; }
function getEffectivePrice(p) { return p.discounted_price != null && p.discounted_price !== "" ? p.discounted_price : p.price; }

export default function CatalogPage() {
  const [packs, setPacks] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All Packs");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPack, setSelectedPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PACKS_PER_PAGE);
  const [sortBy, setSortBy] = useState("default");
  const [filterOpen, setFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [ageRange, setAgeRange] = useState([0, 15]);
  const [pageRange, setPageRange] = useState([0, 100]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [starterKit, setStarterKit] = useState(null);
  const [skForm, setSkForm] = useState({ name: "", email: "", whatsapp: "", consent: false });
  const [skSubmitting, setSkSubmitting] = useState(false);
  const [skDone, setSkDone] = useState(false);
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();
  const impressedPacks = useRef(new Set());
  const searchRef = useRef("");

  useEffect(() => {
    getVisitorToken(); getSessionId(); captureSource(); trackEvent("page_visit");
    api.get("/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    api.get("/bundles").then(({ data }) => setBundles(data || [])).catch(() => {});
    api.get("/site-content/homepage").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/announcement").then(({ data }) => setAnnouncement(data)).catch(() => {});
    api.get("/starter-kit").then(({ data }) => setStarterKit(data)).catch(() => {});
    api.get("/reviews").then(({ data }) => setReviews(data || [])).catch(() => {});
  }, []);

  const fetchPacks = useCallback(async (search, category) => {
    try {
      const params = {};
      if (category && category !== "All Packs") params.category = category;
      if (search) params.search = search;
      const { data } = await api.get("/packs", { params });
      setPacks(data);
      setVisibleCount(PACKS_PER_PAGE);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPacks(searchRef.current, selectedCategory); }, [selectedCategory, fetchPacks]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(debounce((q, c) => {
    if (q) trackEvent("search_used", null, { query: q });
    fetchPacks(q, c);
  }, 400), [fetchPacks]);

  function handleSearch(v) { setSearchQuery(v); searchRef.current = v; debouncedFetch(v, selectedCategory); }
  function handleCategoryChange(c) { setSelectedCategory(c); trackEvent("filter_used", null, { category: c }); }
  function handlePackImpression(id) { if (impressedPacks.current.has(id)) return; impressedPacks.current.add(id); trackEvent("pack_impression", id); }

  const bounds = useMemo(() => {
    if (!packs.length) return { maxPrice: 1000, maxAge: 15, maxPages: 100 };
    let mp = 0, ma = 0, mpg = 0;
    for (const p of packs) { const ep = getEffectivePrice(p); if (ep > mp) mp = ep; const a = parseMinAge(p.age_range); if (a > ma) ma = a; const pg = p.page_count || 0; if (pg > mpg) mpg = pg; }
    return { maxPrice: Math.max(mp, 100), maxAge: Math.max(ma, 5), maxPages: Math.max(mpg, 10) };
  }, [packs]);

  const processedPacks = useMemo(() => {
    let r = [...packs];
    if (filtersApplied) {
      r = r.filter((p) => {
        const ep = getEffectivePrice(p); if (ep < priceRange[0] || ep > priceRange[1]) return false;
        const a = parseMinAge(p.age_range); if (a < ageRange[0] || a > ageRange[1]) return false;
        const pg = p.page_count || 0; if (pg > 0 && (pg < pageRange[0] || pg > pageRange[1])) return false;
        return true;
      });
    }
    if (sortBy !== "default") {
      r.sort((a, b) => {
        switch (sortBy) {
          case "price_low": return getEffectivePrice(a) - getEffectivePrice(b);
          case "price_high": return getEffectivePrice(b) - getEffectivePrice(a);
          case "age_low": return parseMinAge(a.age_range) - parseMinAge(b.age_range);
          case "age_high": return parseMinAge(b.age_range) - parseMinAge(a.age_range);
          default: return 0;
        }
      });
    }
    return r;
  }, [packs, sortBy, filtersApplied, priceRange, ageRange, pageRange]);

  const visiblePacks = processedPacks.slice(0, visibleCount);
  const hasMore = visibleCount < processedPacks.length;
  const features = content?.features || [];
  const howItWorks = content?.how_it_works || [];
  const faq = content?.faq || [];

  return (
    <div className="min-h-screen bg-[#FEFEFC]">
      {/* ── Announcement Bar ─────────────────── */}
      {announcement?.enabled && announcement?.text && (
        <div className="bg-slate-800 text-white text-center text-xs sm:text-sm py-2 px-4 font-body">
          {announcement.link ? (
            <a href={announcement.link} className="hover:underline">{announcement.text}</a>
          ) : announcement.text}
        </div>
      )}

      {/* ── Header ──────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-[#F1E7D0]" data-testid="public-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.jpg" alt="Playaway Club" className="w-9 h-9 rounded-full object-cover" />
            <span className="font-heading font-bold text-base sm:text-lg text-slate-800">Playaway Club</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-5 text-sm font-semibold text-slate-600">
            <a href="#packs" className="hover:text-coral transition-colors">Shop</a>
            <a href="#bundles" className="hover:text-coral transition-colors">Bundles</a>
            <a href="#how" className="hover:text-coral transition-colors">How it works</a>
          </nav>
          <div className="flex items-center gap-1">
            <a href="https://instagram.com/playawayclub" target="_blank" rel="noopener noreferrer" data-testid="instagram-bio-badge">
              <Button variant="ghost" size="icon" className="text-pink-500 hover:text-pink-600 h-9 w-9"><Instagram className="w-5 h-5" /></Button>
            </a>
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/login")} data-testid="admin-login-btn" className="h-9 w-9">
              <Settings className="w-4 h-4 text-slate-400" />
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* ── Hero ───────────────────────────── */}
        <section className="bg-gradient-to-b from-[#FFF5F0] to-[#FEFEFC] py-12 sm:py-20 px-4">
          <div className="max-w-5xl mx-auto flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="flex-1 text-center lg:text-left max-w-xl">
              <p className="text-xs sm:text-sm font-semibold text-coral tracking-wide uppercase mb-3">
                {content?.hero_label || "Screen-swap play kits · Ages 3–8"}
              </p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-800 leading-tight tracking-tight">
                {content?.hero_title || "Phone down. Play on."}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-slate-500 font-body leading-relaxed">
                {content?.hero_subtitle || "Story-led pretend-play kits that hold your child for 30 minutes, with 5 minutes of setup. Every kit comes with a story, a play world, and the exact words to say when the screen goes off."}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <a href="#sample">
                  <Button className="bg-coral hover:bg-coral-hover text-white rounded-lg h-12 px-8 text-base font-bold shadow-md shadow-coral/20 w-full sm:w-auto">
                    {content?.hero_btn_primary || "Get a free starter kit"}
                  </Button>
                </a>
                <a href="#packs">
                  <Button variant="outline" className="rounded-lg h-12 px-8 text-base font-semibold border-slate-300 w-full sm:w-auto">
                    {content?.hero_btn_secondary || "Browse play kits"}
                  </Button>
                </a>
              </div>
            </motion.div>
            {/* Hero product previews */}
            {packs.length >= 2 && (
              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
                className="flex gap-4 shrink-0">
                {packs.slice(0, 2).map((p, i) => (
                  <div key={p._id} className={`w-36 sm:w-44 bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer transform ${i === 1 ? "translate-y-6" : ""} hover:scale-105 transition-transform`}
                    onClick={() => setSelectedPack(p)}>
                    {p.images?.[0] ? (
                      <div className="aspect-[3/4] bg-[#F9F5EB] flex items-center justify-center">
                        <img src={`${API_URL}/api/images/${p.images[0].storage_path}`} alt={p.title} className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : <div className="aspect-[3/4] bg-gradient-to-br from-coral-light to-[#FEF3C7]" />}
                    <div className="p-2.5">
                      <p className="font-heading font-bold text-xs text-slate-800 truncate">{p.title}</p>
                      <p className="text-[10px] text-slate-400">{p.age_range}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </div>
        </section>

        {/* ── Feature Icons ──────────────────── */}
        {features.length > 0 && (
          <section className="border-y border-[#F1E7D0] bg-white py-6 px-4">
            <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              {features.map((f, i) => {
                const Icon = FEATURE_ICONS[f.icon] || Sparkles;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-coral/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-coral" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-sm text-slate-800">{f.title}</p>
                      <p className="text-xs text-slate-500 font-body">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── All Packs ──────────────────────── */}
        <section id="packs" className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-4">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800 mb-5">All play kits</h2>

          {/* Category Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide -mx-4 px-4" data-testid="category-tabs-container">
            {["All Packs", ...categories].map((cat) => (
              <button key={cat} onClick={() => handleCategoryChange(cat)}
                className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-all shrink-0 ${
                  selectedCategory === cat ? "bg-coral text-white shadow-sm" : "bg-white text-slate-600 hover:bg-slate-50 border border-[#F1E7D0]"
                }`} data-testid={`category-tab-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                {cat}
              </button>
            ))}
          </div>

          {/* Sort & Filter */}
          <div className="flex gap-2 mb-4 mt-2">
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setVisibleCount(PACKS_PER_PAGE); }}>
              <SelectTrigger className="h-9 rounded-lg bg-white border-[#F1E7D0] text-sm font-semibold w-auto min-w-[130px] px-3" data-testid="sort-select">
                <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-slate-500 shrink-0" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" className="h-9 rounded-lg border-[#F1E7D0] text-sm font-semibold px-4 relative"
              onClick={() => setFilterOpen(true)} data-testid="filter-btn">
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> Filter
              {filtersApplied && <span className="absolute -top-1 -right-1 w-4 h-4 bg-coral text-white text-[10px] font-bold rounded-full flex items-center justify-center">1</span>}
            </Button>
            {(filtersApplied || sortBy !== "default") && (
              <Button variant="ghost" className="h-9 rounded-lg text-xs text-slate-500 px-3"
                onClick={() => { setSortBy("default"); setPriceRange([0, bounds.maxPrice]); setAgeRange([0, bounds.maxAge]); setPageRange([0, bounds.maxPages]); setFiltersApplied(false); }}>
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-5">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search packs..." className="pl-10 rounded-lg bg-white border-[#F1E7D0] h-11"
              value={searchQuery} onChange={(e) => handleSearch(e.target.value)} data-testid="search-input" />
          </div>

          {/* Grid */}
          <div data-testid="pack-grid">
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[1,2,3,4].map((i) => <div key={i} className="bg-white rounded-2xl h-64 animate-pulse" />)}
              </div>
            ) : processedPacks.length === 0 ? (
              <div className="text-center py-16">
                <Sparkles className="w-10 h-10 text-coral/30 mx-auto mb-3" />
                <p className="text-slate-500 font-body text-lg">{filtersApplied ? "No packs match your filters." : "No packs found."}</p>
                {filtersApplied && <Button variant="outline" className="mt-3 rounded-lg" onClick={() => { setFiltersApplied(false); }}>Clear Filters</Button>}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {visiblePacks.map((pack) => (
                    <div key={pack._id} className="min-w-0">
                      <PackCard pack={pack} onDetailTap={setSelectedPack} onImpression={handlePackImpression} apiUrl={API_URL} compact />
                    </div>
                  ))}
                </div>
                {hasMore && (
                  <div className="mt-6 text-center">
                    <Button variant="outline" onClick={() => setVisibleCount((c) => c + PACKS_PER_PAGE)}
                      className="rounded-lg px-8 font-semibold border-[#F1E7D0]" data-testid="show-more-btn">
                      Show More <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* ── Bundles ────────────────────────── */}
        {bundles.length > 0 && (
          <section id="bundles" className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800 mb-6">Bundles: more play, better price</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {bundles.map((b) => (
                <div key={b._id} className="bg-white rounded-2xl border border-[#F1E7D0] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  {/* Pack cover thumbnails */}
                  <div className="flex gap-1 p-3 pb-0 overflow-hidden">
                    {(b.pack_previews || []).slice(0, 4).map((pp) => (
                      <div key={pp.id} className="flex-1 aspect-[3/4] bg-[#F9F5EB] rounded-lg overflow-hidden">
                        {pp.cover ? (
                          <img src={`${API_URL}/api/images/${pp.cover}`} alt={pp.title} className="w-full h-full object-contain" />
                        ) : <div className="w-full h-full bg-gradient-to-br from-coral-light to-[#FEF3C7]" />}
                      </div>
                    ))}
                  </div>
                  <div className="p-4 pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-green-100 text-green-700 border-0 text-xs font-bold rounded-md">Save ₹{b.savings}</Badge>
                      <span className="text-xs text-slate-400">{b.pack_count} packs</span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-slate-800">{b.name}</h3>
                    <p className="text-sm text-slate-500 font-body mt-1 line-clamp-2">{b.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-baseline gap-2">
                        <span className="font-heading font-bold text-xl text-coral">₹{b.bundle_price}</span>
                        <span className="text-sm text-slate-400 line-through">₹{b.original_price}</span>
                      </div>
                      <Button className="bg-slate-800 hover:bg-slate-900 text-white rounded-lg h-9 px-5 text-sm font-bold"
                        onClick={() => trackEvent("bundle_click", null, { bundle: b.name })}>
                        Get the bundle
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── How It Works ───────────────────── */}
        {howItWorks.length > 0 && (
          <section id="how" className="bg-white border-y border-[#F1E7D0] py-12 px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800 mb-8 text-center">How it works</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {howItWorks.map((s) => (
                  <div key={s.step} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-coral/10 text-coral font-heading font-bold text-xl flex items-center justify-center mx-auto mb-3">
                      {s.step}
                    </div>
                    <h4 className="font-heading font-bold text-sm text-slate-800">{s.title}</h4>
                    <p className="text-xs text-slate-500 font-body mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── FAQ ────────────────────────────── */}
        {faq.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800 mb-6">Questions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {faq.map((f, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#F1E7D0] p-5">
                  <h4 className="font-heading font-bold text-sm text-slate-800 mb-2">{f.q}</h4>
                  <p className="text-sm text-slate-500 font-body leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Reviews ────────────────────────── */}
        {reviews.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800 mb-6">What parents say</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.map((r) => (
                <div key={r._id} className="bg-white rounded-2xl border border-[#F1E7D0] p-5 space-y-3">
                  {r.photos?.length > 0 && (
                    <div className="flex gap-2">
                      {r.photos.map((p) => (
                        <div key={p.id} className="w-16 h-16 rounded-xl overflow-hidden bg-[#F9F5EB]">
                          <img src={`${API_URL}/api/images/${p.storage_path}`} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-slate-700 font-body leading-relaxed">"{r.text}"</p>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((s) => (
                      <svg key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "text-amber-400" : "text-slate-200"}`}
                        fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <div>
                    <p className="font-heading font-bold text-xs text-slate-800">{r.parent_name}</p>
                    <p className="text-[10px] text-slate-400">
                      {[r.city, r.child_age ? `Child age ${r.child_age}` : "", r.pack_title].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Custom Order ───────────────────── */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
          <div className="bg-gradient-to-r from-[#FEEAE6] via-[#FEF3C7] to-[#D6E7FE] rounded-2xl p-6 sm:p-8 text-center" data-testid="custom-order-banner">
            <h3 className="font-heading font-bold text-xl text-slate-800 mb-2">Need something custom?</h3>
            <p className="text-sm text-slate-600 mb-4 font-body max-w-md mx-auto">
              A pack built around your child's name, interests or birthday theme.
            </p>
            <Button onClick={() => { trackEvent("dm_click", null, { type: "custom_order" }); window.open("https://ig.me/m/playawayclub", "_blank"); }}
              className="bg-coral hover:bg-coral-hover text-white rounded-lg px-6 h-11 font-bold">
              <Instagram className="w-4 h-4 mr-2" /> Message us on Instagram
            </Button>
          </div>
        </section>

        {/* ── Free Starter Kit Form ──────────── */}
        {starterKit?.enabled && (
          <section id="sample" className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
            <div className="bg-white rounded-2xl border border-[#F1E7D0] p-6 sm:p-8 max-w-lg mx-auto">
              {skDone ? (
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Download className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-slate-800">Check your email!</h3>
                  <p className="text-sm text-slate-500 font-body">
                    We've sent your free {starterKit.title || "starter kit"} to <strong>{skForm.email}</strong>
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-heading font-bold text-xl text-slate-800 mb-1">
                    {starterKit.title || "Try before you buy: free sample"}
                  </h3>
                  {starterKit.description && (
                    <p className="text-sm text-slate-500 font-body mb-4">{starterKit.description}</p>
                  )}
                  <div className="space-y-3">
                    <Input placeholder="Your name" value={skForm.name}
                      onChange={(e) => setSkForm({ ...skForm, name: e.target.value })}
                      className="rounded-lg h-11" required data-testid="starter-name" />
                    <Input type="email" placeholder="Email for your PDF" value={skForm.email}
                      onChange={(e) => setSkForm({ ...skForm, email: e.target.value })}
                      className="rounded-lg h-11" required data-testid="starter-email" />
                    <Input type="tel" placeholder="WhatsApp number, e.g. +91 98765 43210" value={skForm.whatsapp}
                      onChange={(e) => setSkForm({ ...skForm, whatsapp: e.target.value })}
                      className="rounded-lg h-11" data-testid="starter-whatsapp" />
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={skForm.consent}
                        onChange={(e) => setSkForm({ ...skForm, consent: e.target.checked })}
                        className="mt-1 rounded" />
                      <span className="text-xs text-slate-500 font-body">
                        Send me play ideas and offers
                      </span>
                    </label>
                    <Button onClick={async () => {
                      if (!skForm.name.trim() || !skForm.email.trim()) { toast.error("Name and email are required"); return; }
                      setSkSubmitting(true);
                      try {
                        await api.post("/starter-kit/signup", { ...skForm, source: captureSource() });
                        setSkDone(true);
                        trackEvent("starter_kit_signup");
                      } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
                      finally { setSkSubmitting(false); }
                    }} disabled={skSubmitting}
                      className="w-full bg-coral hover:bg-coral-hover text-white rounded-lg h-12 font-bold text-base"
                      data-testid="starter-submit">
                      {skSubmitting ? "Sending..." : "Get my free kit"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </section>
        )}
      </main>

      {/* ── Footer ───────────────────────────── */}
      <footer className="border-t border-[#F1E7D0] bg-white py-8 px-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.jpg" alt="Playaway Club" className="w-7 h-7 rounded-full" />
            <span className="font-heading font-bold text-sm text-slate-700">Playaway Club</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400 font-body">
            <Link to="/policies/refund-policy" className="hover:text-slate-600">Refund policy</Link>
            <Link to="/policies/terms" className="hover:text-slate-600">Terms</Link>
            <Link to="/policies/privacy" className="hover:text-slate-600">Privacy</Link>
            <Link to="/policies/contact" className="hover:text-slate-600">Contact</Link>
          </div>
          <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} Playaway Club</p>
        </div>
      </footer>

      {/* ── Filter Sheet ─────────────────────── */}
      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh]" data-testid="filter-sheet">
          <SheetHeader className="pb-2"><SheetTitle className="font-heading text-xl">Filter Packs</SheetTitle></SheetHeader>
          <div className="space-y-6 py-4 overflow-y-auto">
            <div>
              <div className="flex justify-between mb-3"><label className="text-sm font-semibold text-slate-700">Price Range</label><span className="text-sm text-coral font-bold">₹{priceRange[0]} – ₹{priceRange[1]}</span></div>
              <Slider value={priceRange} onValueChange={setPriceRange} min={0} max={Math.ceil(bounds.maxPrice / 50) * 50} step={10} />
            </div>
            <div>
              <div className="flex justify-between mb-3"><label className="text-sm font-semibold text-slate-700">Starting Age</label><span className="text-sm text-coral font-bold">{ageRange[0]} – {ageRange[1]} yrs</span></div>
              <Slider value={ageRange} onValueChange={setAgeRange} min={0} max={Math.max(bounds.maxAge, 12)} step={1} />
            </div>
            <div>
              <div className="flex justify-between mb-3"><label className="text-sm font-semibold text-slate-700">Pages</label><span className="text-sm text-coral font-bold">{pageRange[0]} – {pageRange[1]}</span></div>
              <Slider value={pageRange} onValueChange={setPageRange} min={0} max={Math.ceil(bounds.maxPages / 5) * 5} step={1} />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 rounded-lg h-11" onClick={() => { setFiltersApplied(false); setFilterOpen(false); }}>Clear</Button>
              <Button className="flex-1 rounded-lg h-11 bg-coral hover:bg-coral-hover text-white font-bold" onClick={() => { setFiltersApplied(true); setVisibleCount(PACKS_PER_PAGE); setFilterOpen(false); }}>Apply</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <PackDetailDrawer pack={selectedPack} open={!!selectedPack} onClose={() => setSelectedPack(null)} apiUrl={API_URL} />
    </div>
  );
}
